import * as R from 'ramda'
import { useMemo } from 'react'
import { Timestamp } from '@weblite/ts-action/types'
import { Message } from '@weblite/ts-action/message'
import {
  useOpenedRoomId,
  useOpenedRoomLoading,
} from '$logic/opened-room/react-hook'
import {
  useOpenedRoomPinnedMessagesIids,
  usePinContentByIids,
} from '$logic/pin'
import { haveDifferentDates } from '$comp/_app/components/chat/components/body/components/messageList/react-hooks'
import { PendingEditUpload } from '$logic/pending-edit-uploads/types'
import { dateWithOffset } from '$setup/time-offset'

export type PosInBlock = 'START' | 'MID' | 'END' | 'SINGLE'

export interface IDateSeparator {
  type: 'DATE_SEPARATOR'
  date: Timestamp
}

export type MessageOrPendingEditUpload = Message | PendingEditUpload
type MessageOrPendingEditUploadOrDateSeparator =
  | IDateSeparator
  | MessageOrPendingEditUpload

export type PinListItem = MessageOrPendingEditUploadOrDateSeparator & {
  posInBlock: PosInBlock
  showSender: boolean
}

const getMessageDate = (
  message?: MessageOrPendingEditUploadOrDateSeparator,
): number => {
  if (!message) return dateWithOffset()
  if ('uploadId' in message) return message.pendingDate || dateWithOffset()
  return message.date || dateWithOffset()
}

const createDateSeparator = (date: Timestamp): IDateSeparator => ({
  type: 'DATE_SEPARATOR',
  date,
})

export function useOpenedRoomPinList(updateKey: string): {
  loading: boolean
  messageList: PinListItem[]
} {
  const loading = useOpenedRoomLoading()
  const openedRoomPinMessageIids = useOpenedRoomPinnedMessagesIids()
  const roomId = useOpenedRoomId()
  const { messages: openedRoomMessages, loading: messageLoading } =
    usePinContentByIids(roomId, openedRoomPinMessageIids, updateKey) // TODO: performance issue
  const messagesWithExtraItems = useMemo(
    () =>
      loading || messageLoading || !openedRoomMessages
        ? []
        : R.compose<
            MessageOrPendingEditUpload[],
            MessageOrPendingEditUpload[],
            MessageOrPendingEditUploadOrDateSeparator[],
            MessageOrPendingEditUploadOrDateSeparator[][],
            PinListItem[][],
            PinListItem[]
          >(
            R.unnest,
            R.map(block =>
              block.map((blockItem, index) => ({
                ...blockItem,
                showSender: index === 0,
                posInBlock:
                  block.length === 1
                    ? 'SINGLE'
                    : index === 0
                      ? 'START'
                      : index === block.length - 1
                        ? 'END'
                        : 'MID',
              })),
            ),
            R.groupWith((msg1, msg2) => {
              const firstSender = 'sender' in msg1 && msg1.sender
              const secondSender = 'sender' in msg2 && msg2.sender
              return (
                firstSender &&
                secondSender &&
                firstSender.type === 'USER' &&
                firstSender.type === secondSender.type &&
                firstSender.id === secondSender.id
              )
            }),
            messages =>
              R.reduce(
                (acc, msg) => {
                  const message = [msg]
                  if (!R.length(acc))
                    return [
                      createDateSeparator(getMessageDate(msg)),
                      ...message,
                    ]
                  const lastPrev = R.last(acc)
                  if (
                    haveDifferentDates(
                      new Date(getMessageDate(lastPrev)),
                      new Date(getMessageDate(msg)),
                    )
                  )
                    return R.concat(acc, [
                      createDateSeparator(getMessageDate(msg)),
                      ...message,
                    ])
                  return R.concat(acc, message)
                },
                [] as MessageOrPendingEditUploadOrDateSeparator[],
                messages,
              ),

            (...val) =>
              R.reject((message: MessageOrPendingEditUpload) =>
                Boolean('patches' in message && message.patches?.deleted),
              )(val),
          )(...openedRoomMessages),
    [openedRoomMessages, loading],
  )

  return {
    loading,
    messageList: messagesWithExtraItems,
  }
}
